/**
 * Measurement computation engine.
 *
 * Pure functional orchestrator that dispatches to individual calculators.
 * No state, no side effects — fully testable.
 */

import type { Vec3 } from '../../types';
import { MeasurementResult } from '../measurement-result';
import { computeAngle } from './angle-calculator';
import { computeDerived } from './derived-calculator';
import { computeDiameterAtNode, computeOffsetDiameter, computeMinMaxDiameter } from './diameter-calculator';
import type { KeypointPlacement } from '../keypoint-placement';
import { computeLength } from './length-calculator';
import { findPathContainingNodes } from './path-resolver';
import type { ComputationContext } from './types';
import {
    MeasurementType,
    MeasurementUnit,
    type IMeasurementDefinition,
    type ILengthMeasurementDef,
    type IDiameterMeasurementDef,
    type IOffsetDiameterMeasurementDef,
    type IMinMaxDiameterMeasurementDef,
    type IAngleMeasurementDef,
    type IDerivedMeasurementDef,
} from '../templates/types';

// ============================================================================
// Public API
// ============================================================================

export class MeasurementEngine {
    /**
     * Compute all measurements defined in the template + custom offsets.
     *
     * Processes non-derived measurements first, then derived ones
     * (which may depend on previously computed values).
     */
    static computeAll(ctx: ComputationContext): Map<string, MeasurementResult> {
        const results = new Map<string, MeasurementResult>();
        const allDefs = [...ctx.template.measurements, ...ctx.customOffsets];

        // Pass 1: non-derived measurements
        for (const def of allDefs) {
            if (def.type !== MeasurementType.DERIVED) {
                const result = MeasurementEngine.computeOne(def, ctx, results);
                if (result) results.set(result.id, result);
            }
        }

        // Pass 2: derived measurements (dependencies already resolved)
        for (const def of allDefs) {
            if (def.type === MeasurementType.DERIVED) {
                const result = MeasurementEngine.computeOne(def, ctx, results);
                if (result) results.set(result.id, result);
            }
        }

        return results;
    }

    /**
     * Recompute a subset of measurements (for incremental updates).
     *
     * @param measurementIds - IDs of measurements to recompute
     * @param existing - Current measurement results (used for derived dependency resolution)
     */
    static recompute(
        measurementIds: ReadonlySet<string>,
        ctx: ComputationContext,
        existing: Map<string, MeasurementResult>
    ): Map<string, MeasurementResult> {
        const allDefs = [...ctx.template.measurements, ...ctx.customOffsets];
        const defsToCompute = allDefs.filter((d) => measurementIds.has(d.id));

        // Non-derived first
        for (const def of defsToCompute) {
            if (def.type !== MeasurementType.DERIVED) {
                const result = MeasurementEngine.computeOne(def, ctx, existing);
                if (result) existing.set(result.id, result);
            }
        }

        // Derived second
        for (const def of defsToCompute) {
            if (def.type === MeasurementType.DERIVED) {
                const result = MeasurementEngine.computeOne(def, ctx, existing);
                if (result) existing.set(result.id, result);
            }
        }

        return existing;
    }

    /**
     * Compute a single measurement from its definition.
     */
    static computeOne(
        def: IMeasurementDefinition,
        ctx: ComputationContext,
        resolvedMeasurements: ReadonlyMap<string, MeasurementResult>
    ): MeasurementResult | null {
        switch (def.type) {
            case MeasurementType.LENGTH:
                return computeLengthMeasurement(def, ctx);
            case MeasurementType.DIAMETER:
                return computeDiameterMeasurement(def, ctx);
            case MeasurementType.OFFSET_DIAMETER:
                return computeOffsetDiameterMeasurement(def, ctx);
            case MeasurementType.MIN_MAX_DIAMETER:
                return computeMinMaxDiameterMeasurement(def, ctx);
            case MeasurementType.ANGLE:
                return computeAngleMeasurement(def, ctx);
            case MeasurementType.DERIVED:
                return computeDerivedMeasurement(def, ctx, resolvedMeasurements);
        }
    }
}

// ============================================================================
// Ghost-aware node pair resolution
// ============================================================================

/**
 * Resolve a pair of keypoint IDs to node IDs that lie on a common centerline.
 *
 * Tries primary placements first (existing behavior). If no single centerline
 * contains both, tries combinations with ghost placements.
 */
function resolveNodePair(
    keypointIdA: string,
    keypointIdB: string,
    ctx: ComputationContext
): { placementA: KeypointPlacement; placementB: KeypointPlacement } | null {
    const primaryA = ctx.placements.get(keypointIdA);
    const primaryB = ctx.placements.get(keypointIdB);
    if (!primaryA || !primaryB) return null;

    // Try primary placements first
    if (findPathContainingNodes(primaryA.nodeId, primaryB.nodeId, ctx.centerlines)) {
        return { placementA: primaryA, placementB: primaryB };
    }

    // Try ghost A with primary B
    const ghostsA = ctx.ghostPlacements.get(keypointIdA);
    if (ghostsA) {
        for (const ghostA of ghostsA) {
            if (findPathContainingNodes(ghostA.nodeId, primaryB.nodeId, ctx.centerlines)) {
                return { placementA: ghostA, placementB: primaryB };
            }
        }
    }

    // Try primary A with ghost B
    const ghostsB = ctx.ghostPlacements.get(keypointIdB);
    if (ghostsB) {
        for (const ghostB of ghostsB) {
            if (findPathContainingNodes(primaryA.nodeId, ghostB.nodeId, ctx.centerlines)) {
                return { placementA: primaryA, placementB: ghostB };
            }
        }
    }

    // Try ghost A with ghost B
    if (ghostsA && ghostsB) {
        for (const ghostA of ghostsA) {
            for (const ghostB of ghostsB) {
                if (findPathContainingNodes(ghostA.nodeId, ghostB.nodeId, ctx.centerlines)) {
                    return { placementA: ghostA, placementB: ghostB };
                }
            }
        }
    }

    return null;
}

// ============================================================================
// Individual measurement builders
// ============================================================================

function computeLengthMeasurement(
    def: ILengthMeasurementDef,
    ctx: ComputationContext
): MeasurementResult | null {
    const resolved = resolveNodePair(def.fromKeypointId, def.toKeypointId, ctx);
    if (!resolved) return null;

    const { placementA: fromPlacement, placementB: toPlacement } = resolved;

    const result = computeLength(fromPlacement.nodeId, toPlacement.nodeId, ctx.centerlines);
    if (!result) return null;

    return new MeasurementResult(
        def.id,
        def.type,
        def.category,
        def.name,
        def.shortLabel,
        result.value,
        MeasurementUnit.MM,
        result.fromNodeId,
        fromPlacement.isEdited || toPlacement.isEdited,
        { type: 'length', fromNodeId: result.fromNodeId, toNodeId: result.toNodeId }
    );
}

function computeDiameterMeasurement(
    def: IDiameterMeasurementDef,
    ctx: ComputationContext
): MeasurementResult | null {
    const placement = ctx.placements.get(def.keypointId);
    if (!placement) return null;

    const result = computeDiameterAtNode(placement.nodeId, ctx.nodePool, ctx.diameterEdits);
    if (!result) return null;

    return new MeasurementResult(
        def.id,
        def.type,
        def.category,
        def.name,
        def.shortLabel,
        result.value,
        MeasurementUnit.MM,
        result.nodeId,
        result.isEdited || placement.isEdited,
        { type: 'diameter', keypointId: def.keypointId, nodeId: result.nodeId }
    );
}

function computeOffsetDiameterMeasurement(
    def: IOffsetDiameterMeasurementDef,
    ctx: ComputationContext
): MeasurementResult | null {
    const placement = ctx.placements.get(def.keypointId);
    if (!placement) return null;

    const result = computeOffsetDiameter(
        placement.nodeId,
        def.keypointId,
        def.offsetMm,
        ctx.centerlines,
        ctx.diameterEdits
    );
    if (!result) return null;

    return new MeasurementResult(
        def.id,
        def.type,
        def.category,
        def.name,
        def.shortLabel,
        result.value,
        MeasurementUnit.MM,
        result.nodeId,
        result.isEdited || placement.isEdited,
        { type: 'offset-diameter', keypointId: def.keypointId, nodeId: result.nodeId, offsetMm: def.offsetMm }
    );
}

function computeMinMaxDiameterMeasurement(
    def: IMinMaxDiameterMeasurementDef,
    ctx: ComputationContext
): MeasurementResult | null {
    const resolved = resolveNodePair(def.fromKeypointId, def.toKeypointId, ctx);
    if (!resolved) return null;

    const { placementA: fromPlacement, placementB: toPlacement } = resolved;

    const result = computeMinMaxDiameter(
        fromPlacement.nodeId,
        toPlacement.nodeId,
        def.mode,
        ctx.centerlines,
        ctx.diameterEdits
    );
    if (!result) return null;

    return new MeasurementResult(
        def.id,
        def.type,
        def.category,
        def.name,
        def.shortLabel,
        result.value,
        MeasurementUnit.MM,
        result.nodeId,
        result.isEdited || fromPlacement.isEdited || toPlacement.isEdited,
        {
            type: 'min-max-diameter',
            nodeId: result.nodeId,
            fromNodeId: result.fromNodeId,
            toNodeId: result.toNodeId,
        }
    );
}

function computeAngleMeasurement(
    def: IAngleMeasurementDef,
    ctx: ComputationContext
): MeasurementResult | null {
    const positions: Vec3[] = [];
    let hasEdited = false;

    for (const kpId of def.keypointIds) {
        const placement = ctx.placements.get(kpId);
        if (!placement) return null;
        positions.push(placement.worldPosition);
        if (placement.isEdited) hasEdited = true;
    }

    const angleDeg = computeAngle(positions as [Vec3, Vec3, Vec3], def.formula);

    // Navigate to the middle keypoint (vertex)
    const vertexPlacement = ctx.placements.get(def.keypointIds[1]);

    return new MeasurementResult(
        def.id,
        def.type,
        def.category,
        def.name,
        def.shortLabel,
        angleDeg,
        MeasurementUnit.DEGREES,
        vertexPlacement?.nodeId ?? null,
        hasEdited,
        { type: 'angle', keypointIds: def.keypointIds }
    );
}

function computeDerivedMeasurement(
    def: IDerivedMeasurementDef,
    _ctx: ComputationContext,
    resolvedMeasurements: ReadonlyMap<string, MeasurementResult>
): MeasurementResult | null {
    const value = computeDerived(def, resolvedMeasurements);
    if (value === null) return null;

    // Check if any dependency has edited inputs
    let hasEdited = false;
    for (const depId of def.dependencies) {
        const dep = resolvedMeasurements.get(depId);
        if (dep?.hasEditedInputs) {
            hasEdited = true;
            break;
        }
    }

    // Navigate to first dependency's navigation node
    const firstDep = resolvedMeasurements.get(def.dependencies[0]);

    return new MeasurementResult(
        def.id,
        def.type,
        def.category,
        def.name,
        def.shortLabel,
        value,
        MeasurementUnit.PERCENT,
        firstDep?.navigationNodeId ?? null,
        hasEdited,
        { type: 'derived', dependencies: def.dependencies }
    );
}
